// Global Instructions Rule Applied!
//
// Edit a booked capital call receipt.
//
// A payment books its Capital Call Receipt the instant it is recorded, so the
// fund transaction is read-only and its Unbook button is refused server-side.
// Correcting one used to mean deleting it and recording it again, which mints
// a new transaction id. This modal drives cc__update_contribution instead: the
// server unbooks the receipt, rewrites it, regenerates the journals and books
// it again in one transaction, keeping both ids.
//
// Opened from two places - the fund's Capital Calls tab and a Capital Call
// Receipt fund transaction - so it takes only a contribution id and reads the
// row itself. That is also what keeps it correct on a cold load: there is no
// upstream list to populate it from.

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Form, InputNumber, Modal, Select, Spin, Typography } from 'antd'
import DatePicker from '../../../core/components/DatePicker.tsx'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { useNumberDisplay } from '../../../core/utils/inputNumberFormat.ts'
import {
  fetchContributionDetail,
  fetchFundCommitmentOptions,
  updateContribution,
  type CommitmentOption,
  type UpdateContributionPayload
} from '../services/capitalCallsService.ts'
import type { FundContributionRow } from '../../../state_manager/data_models/capital_call_detail.ts'

const { Text } = Typography

interface Props {
  /** null closes the modal; a contribution id opens it on that payment. */
  contributionId: string | null
  onClose: () => void
  /** the caller refetches - paid status and the position both move. */
  onSaved: () => void
}

interface FormValues {
  fund_commitment_id?: string
  amount?: number
  payment_date?: dayjs.Dayjs
}

export function EditContributionModal({ contributionId, onClose, onSaved }: Props): React.ReactElement {
  const [form] = Form.useForm<FormValues>()
  // static Modal.confirm does not render under React 19 - hook form required
  const [modalApi, modalContextHolder] = Modal.useModal()
  const [row, setRow] = useState<FundContributionRow | null>(null)
  const [options, setOptions] = useState<CommitmentOption[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [changingInvestor, setChangingInvestor] = useState(false)
  const currencyText = useNumberDisplay('currency')

  const open = contributionId != null

  useEffect(() => {
    if (!contributionId) {
      setRow(null)
      setOptions([])
      setChangingInvestor(false)
      return
    }
    let cancelled = false
    setLoading(true)
    ;(async () => {
      const token = getAccessToken(store.getState().auth)
      const res = await fetchContributionDetail(contributionId, token)
      if (cancelled) return
      if (res.error || !res.data) {
        showErrorToast(res.error || 'Failed to load the payment')
        setLoading(false)
        onClose()
        return
      }
      setRow(res.data)
      form.setFieldsValue({
        fund_commitment_id: res.data.fund_commitments__id,
        amount: res.data.capital_contributions__amount ?? undefined,
        payment_date: res.data.capital_contributions__payment_date
          ? dayjs(res.data.capital_contributions__payment_date)
          : undefined
      })
      setLoading(false)
      // a payment can only move between commitments in the SAME fund, so the
      // options are fund-scoped to the one the payment already sits in
      if (res.data.funds__id) {
        const opts = await fetchFundCommitmentOptions(res.data.funds__id, token)
        if (!cancelled && !opts.error) setOptions(opts.data)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [contributionId, form, onClose])

  const isTransferDerived = useMemo(
    () => Boolean(row?.capital_contributions__fund_group_transfer_id),
    [row]
  )

  const submit = useCallback(
    async (confirmDuplicate: boolean) => {
      if (!contributionId || !row) return
      const values = await form.validateFields()
      if (!values.amount || !values.payment_date) return

      const payload: UpdateContributionPayload = {
        amount: values.amount,
        payment_date: values.payment_date.format('YYYY-MM-DD'),
        confirm_duplicate: confirmDuplicate
      }
      // only travels on the wire when the investor was actually changed
      if (values.fund_commitment_id && values.fund_commitment_id !== row.fund_commitments__id) {
        payload.fund_commitment_id = values.fund_commitment_id
      }

      setSaving(true)
      try {
        const token = getAccessToken(store.getState().auth)
        const res = await updateContribution(contributionId, payload, token)

        if (res.duplicateWarning) {
          const duplicates = (res.data?.duplicates ?? []) as Array<{
            amount?: number
            payment_date?: string
            investor_name?: string
          }>
          modalApi.confirm({
            title: 'Possible duplicate payment',
            content: (
              <div>
                <p>{res.data?.status_message ?? 'These values match a payment already recorded.'}</p>
                <p className='mb-1'>Nothing has been written yet.</p>
                <ul className='mb-2 pl-4'>
                  {duplicates.map((d, i) => (
                    <li key={`${d.payment_date ?? ''}-${i}`}>
                      {d.investor_name ?? 'Investor'} — {currencyText(d.amount ?? 0)} on{' '}
                      {d.payment_date ? dayjs(d.payment_date).format('MM/DD/YYYY') : '-'}
                    </li>
                  ))}
                </ul>
              </div>
            ),
            okText: 'Save anyway',
            onOk: async () => {
              await submit(true)
            }
          })
          return
        }

        if (res.error) {
          // the server's message IS the instruction (a closed financial year,
          // a transfer-derived row) - leave the modal open so it can be acted on
          showErrorToast(res.error)
          return
        }

        toast.success(
          res.data?.status === 'info' ? 'No changes to save' : 'Payment updated and receipt re-booked'
        )
        onSaved()
        onClose()
      } finally {
        setSaving(false)
      }
    },
    [contributionId, row, form, modalApi, currencyText, onSaved, onClose]
  )

  return (
    <Modal
      open={open}
      title={`Edit payment${row?.investors__name ? ` — ${row.investors__name}` : ''}`}
      onCancel={onClose}
      onOk={() => void submit(false)}
      okText='Save payment'
      okButtonProps={{ disabled: loading || isTransferDerived }}
      confirmLoading={saving}
      destroyOnHidden
    >
      {modalContextHolder}
      {loading ? (
        <div className='py-8 text-center'>
          <Spin />
        </div>
      ) : (
        <Form form={form} layout='vertical' preserve={false}>
          {isTransferDerived ? (
            <Alert
              type='warning'
              showIcon
              className='mb-3'
              message="This payment was generated by an intercompany transfer and has no receipt of its own — revert the transfer from the consolidated fund's Calls tab instead."
            />
          ) : (
            <Alert
              type='info'
              showIcon
              className='mb-3'
              message='Saving unbooks the receipt, rewrites it and books it again. The paid status reflows.'
            />
          )}

          <Form.Item label='Investor'>
            {changingInvestor ? (
              <Form.Item name='fund_commitment_id' noStyle rules={[{ required: true, message: 'Investor is required' }]}>
                <Select showSearch optionFilterProp='label' options={options} placeholder='Select investor' />
              </Form.Item>
            ) : (
              <div className='flex items-center gap-2'>
                <Text>{row?.investors__name ?? '-'}</Text>
                <button
                  type='button'
                  className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-sm'
                  onClick={() => setChangingInvestor(true)}
                  disabled={isTransferDerived}
                >
                  Change investor
                </button>
              </div>
            )}
          </Form.Item>

          <Form.Item name='amount' label='Amount' rules={[{ required: true, message: 'Amount is required' }]}>
            <InputNumber<number>
              style={{ width: '100%' }}
              min={0.01}
              disabled={isTransferDerived}
              formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(v) => (v ? Number(v.replace(/,/g, '')) : 0)}
            />
          </Form.Item>

          <Form.Item
            name='payment_date'
            label='Payment Date'
            rules={[{ required: true, message: 'Payment date is required' }]}
          >
            <DatePicker style={{ width: '100%' }} format='MM/DD/YYYY' disabled={isTransferDerived} />
          </Form.Item>
        </Form>
      )}
    </Modal>
  )
}

export default EditContributionModal
